import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Sparkles, AlertTriangle, TrendingUp, ShieldAlert, Users as UsersIcon, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const PRIORITY_STYLES = {
  high: {
    badge: 'bg-red-100 text-red-700 border-red-200',
    border: 'border-l-red-500',
  },
  medium: {
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    border: 'border-l-amber-500',
  },
  low: {
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    border: 'border-l-emerald-500',
  },
};

const CATEGORY_ICONS = {
  fraud_detection: ShieldAlert,
  risk_analysis: AlertTriangle,
  revenue_optimization: TrendingUp,
  customer_behavior: UsersIcon,
  opportunity: TrendingUp,
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
};

const prettifyCategory = (c) => {
  if (!c) return '';
  return c.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const AIInsightDigest = () => {
  const navigate = useNavigate();
  const { token, selectedPlatform, backendUrl } = useAuth();
  const [open, setOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalNew, setTotalNew] = useState(0);

  const fetchDigest = useCallback(async () => {
    if (!token || !selectedPlatform) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, 'X-Portal-Background': '1' };

      // 1) Authoritative count of "new" insights for the trigger badge.
      const countParams = new URLSearchParams({
        platform_id: selectedPlatform,
        status: 'new',
        per_page: '1',
        page: '1',
      });
      const countRes = await axios.get(`${backendUrl}/api/insights?${countParams}`, { headers });
      const newCount = countRes.data?.total || 0;

      // 2) Top 5 items for the popover list: high-priority new first,
      //    fill with other new if fewer than 5 highs exist.
      const baseParams = {
        platform_id: selectedPlatform,
        status: 'new',
        per_page: '5',
        page: '1',
      };
      const highParams = new URLSearchParams({ ...baseParams, priority: 'high' });
      const highRes = await axios.get(`${backendUrl}/api/insights?${highParams}`, { headers });
      let items = highRes.data?.insights || [];

      if (items.length < 5) {
        const fillParams = new URLSearchParams(baseParams);
        const fillRes = await axios.get(`${backendUrl}/api/insights?${fillParams}`, { headers });
        const fillItems = fillRes.data?.insights || [];
        const seen = new Set(items.map((i) => i.id));
        for (const it of fillItems) {
          if (items.length >= 5) break;
          if (!seen.has(it.id)) {
            items.push(it);
            seen.add(it.id);
          }
        }
      }

      setInsights(items.slice(0, 5));
      setTotalNew(newCount);
      setError(null);
    } catch (err) {
      console.error('[AIInsightDigest] fetch failed:', err);
      setError('Could not load AI insights');
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedPlatform, backendUrl]);

  // Initial + light polling so the trigger badge stays current
  useEffect(() => {
    fetchDigest();
    const intervalId = setInterval(fetchDigest, 5 * 60 * 1000); // 5 min
    return () => clearInterval(intervalId);
  }, [fetchDigest]);

  // Refetch every time the popover opens to keep content fresh
  useEffect(() => {
    if (open) fetchDigest();
  }, [open, fetchDigest]);

  const goToInsights = () => {
    setOpen(false);
    navigate('/dashboard/ai-insights');
  };

  const openInsight = (insightId) => {
    setOpen(false);
    navigate(`/dashboard/ai-insights?insight_id=${encodeURIComponent(insightId)}`);
  };

  const triggerBadge = totalNew > 0 ? (
    totalNew > 99 ? '99+' : String(totalNew)
  ) : null;

  const renderItem = (insight) => {
    const priorityKey = (insight.priority || 'low').toLowerCase();
    const styles = PRIORITY_STYLES[priorityKey] || PRIORITY_STYLES.low;
    const CategoryIcon = CATEGORY_ICONS[insight.category] || Sparkles;
    const headline = insight.title?.trim() || insight.description || 'AI Insight';

    return (
      <button
        key={insight.id}
        type="button"
        onClick={() => openInsight(insight.id)}
        data-testid={`ai-insight-digest-item-${insight.id}`}
        className={`w-full text-left group bg-white hover:bg-gray-50 transition-colors border border-gray-100 ${styles.border} border-l-4 rounded-lg p-3 flex items-start gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
      >
        <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-md bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center">
          <CategoryIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={`${styles.badge} text-[10px] uppercase tracking-wide px-2 py-0.5`}>
              {priorityKey}
            </Badge>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              {prettifyCategory(insight.category)}
            </span>
            <span className="text-[11px] text-gray-400 ml-auto">
              {formatRelativeTime(insight.created_at)}
            </span>
          </div>
          <p className="text-sm text-gray-800 leading-snug line-clamp-2 group-hover:text-gray-900">
            {headline}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 mt-1 flex-shrink-0" />
      </button>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          data-testid="ai-insight-digest-trigger"
          aria-label="AI Insights"
          title="AI Insights"
        >
          <Sparkles className="h-5 w-5" />
          {triggerBadge && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white"
              data-testid="ai-insight-digest-trigger-badge"
            >
              {triggerBadge}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(420px,calc(100vw-2rem))] p-0 overflow-hidden shadow-xl border-gray-200"
        data-testid="ai-insight-digest-popover"
      >
        <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-white border-b border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-md bg-gradient-to-br from-indigo-500 to-blue-500 text-white shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 leading-tight">AI Insight Digest</h3>
                <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                  New insights from the agent
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {totalNew > 0 && (
                <Badge
                  className="bg-red-50 text-red-700 border-red-100 text-[10px] px-2 py-0.5"
                  data-testid="ai-insight-digest-unresolved-count"
                >
                  {totalNew} new
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchDigest}
                disabled={loading}
                className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700"
                data-testid="ai-insight-digest-refresh-btn"
                aria-label="Refresh AI insight digest"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        <div className="p-3 max-h-[420px] overflow-y-auto">
          {loading && insights.length === 0 ? (
            <div className="space-y-2" data-testid="ai-insight-digest-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-14 bg-gray-100 rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div
              className="text-center py-6 text-sm text-gray-500"
              data-testid="ai-insight-digest-error"
            >
              {error}
            </div>
          ) : insights.length === 0 ? (
            <div
              className="text-center py-8"
              data-testid="ai-insight-digest-empty"
            >
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-emerald-50 to-green-100 rounded-full flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-sm font-medium text-gray-700">All clear — no unresolved insights.</p>
              <p className="text-xs text-gray-500 mt-1">The agent will surface new signals after its next run.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {insights.map(renderItem)}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToInsights}
            className="w-full justify-center text-xs h-8 hover:bg-white"
            data-testid="ai-insight-digest-view-all-btn"
          >
            View all insights
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AIInsightDigest;
